import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import {
  authLogin,
  authLogout,
  authMe,
  authRegister,
  type RegisterPayload,
  type User,
} from '../services/api';

interface AuthContextValue {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (payload: RegisterPayload) => Promise<void>;
  logout: () => Promise<void>;
  /** v4-9-2: re-fetch /auth/me (e.g. after email verification flips the flag). */
  refreshUser: () => Promise<void>;
  /** v4-9-2: optimistically mark the current user verified after a successful
   *  verify, so the nudge/gate UI updates immediately without a round-trip. */
  markEmailVerified: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    authMe()
      .then((res) => {
        if (active) setUser(res.data);
      })
      .catch(() => {
        if (active) setUser(null);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const login = async (email: string, password: string) => {
    const res = await authLogin(email, password);
    setUser(res.data);
  };

  const register = async (payload: RegisterPayload) => {
    const res = await authRegister(payload);
    setUser(res.data);
  };

  const logout = async () => {
    try {
      await authLogout();
    } finally {
      setUser(null);
    }
  };

  const refreshUser = async () => {
    try {
      const res = await authMe();
      setUser(res.data);
    } catch {
      // Leave the current user as-is on a transient failure.
    }
  };

  const markEmailVerified = () => {
    setUser((u) => (u ? { ...u, is_email_verified: true } : u));
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        register,
        logout,
        refreshUser,
        markEmailVerified,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}
